import { useState, useCallback, useEffect } from 'react';
import { authAPI, setToken, removeToken, getToken } from '../services/api';

const STORAGE_KEY = 'billing_user';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Cargar usuario de localStorage al iniciar
  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEY);
    const token = getToken();

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        removeToken();
      }
    } else {
      // If one exists without the other, clean up
      localStorage.removeItem(STORAGE_KEY);
      removeToken();
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      if (response.data.success) {
        const userData = response.data.user;
        const token = response.data.token;

        // Store token and user data
        setToken(token);
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));

        return { success: true };
      }
      return { success: false, error: 'Error de autenticacion' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error de conexion';
      return { success: false, error: errorMessage };
    }
  }, []);

  // Solicitar un código de acceso por SMS
  const sendCode = useCallback(async (phone) => {
    try {
      const response = await authAPI.sendCode(phone);
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, error: response.data.error || 'No se pudo enviar el código' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error de conexion';
      return { success: false, error: errorMessage };
    }
  }, []);

  // Verificar el código SMS e iniciar sesión
  const loginWithCode = useCallback(async (phone, code) => {
    try {
      const response = await authAPI.verifyCode(phone, code);
      if (response.data.success) {
        const userData = response.data.user;
        const token = response.data.token;

        setToken(token);
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));

        return { success: true };
      }
      return { success: false, error: 'Error de autenticacion' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error de conexion';
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem(STORAGE_KEY);
    removeToken();
  }, []);

  return {
    user,
    isAuthenticated,
    loading,
    login,
    sendCode,
    loginWithCode,
    logout
  };
};
